import { useState, useEffect, useCallback } from 'react';
import { apiFetch, getToken } from '../dppApi';

// The collector's open inquiries (My Collection side panel). Communication with the
// boutique is plain TEXT. When a time is agreed in the conversation, Bloom schedules
// it and the customer sees the scheduled date here (read-only — the boutique manages
// the appointment).
const meetingLabel = (t) => (t === 'virtual' ? 'Virtual call' : t === 'in_store' ? 'In-store visit' : 'Question');
const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return isNaN(d) ? iso : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};
const HISTORY_HEAD = 1;

const UserInquiries = () => {
    const token = getToken('user');
    const [inquiries, setInquiries] = useState([]);
    const [msgs, setMsgs] = useState({});
    const [historyOpen, setHistoryOpen] = useState({});

    const load = useCallback(() => {
        apiFetch('/api/inquiry', { token }).then(setInquiries).catch(() => {});
    }, [token]);

    useEffect(() => {
        load();
        const id = setInterval(load, 15000);
        const onFocus = () => load();
        window.addEventListener('focus', onFocus);
        return () => { clearInterval(id); window.removeEventListener('focus', onFocus); };
    }, [load]);

    const patch = async (id, body) => {
        try { await apiFetch(`/api/inquiry/${id}`, { method: 'PATCH', token, body }); load(); }
        catch { /* leave the panel as-is on failure */ }
    };

    const autoGrow = (el) => { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; };
    const msgText = (r) => (msgs[r.inquiry_id] ?? '');
    const sendMsg = (r) => {
        const text = msgText(r).trim();
        if (!text) return;
        setMsgs(m => { const n = { ...m }; delete n[r.inquiry_id]; return n; });
        // Optimistic: show the message immediately; the backend (detection + reply
        // draft, run concurrently) reconciles when patch() reloads.
        const optimistic = { actor: 'customer', kind: 'messaged', body: text, created_at: new Date().toISOString(), appointment_time: null };
        setInquiries(list => list.map(x => x.inquiry_id === r.inquiry_id
            ? { ...x, events: [...(x.events || []), optimistic] } : x));
        patch(r.inquiry_id, { action: 'message', message: text });
    };

    if (!inquiries.length) return null;

    return (
        <div className="dpp-card insights-card assistant-card maintenance-user-panel inquiries-panel">
            <div className="insights-card-title">Inquiries</div>
            <div className="maintenance-user-list">
                {inquiries.map(r => {
                    // Appointment scheduled/updated events are shown in the "Scheduled · …"
                    // summary at the top, so keep them out of the timeline (and its count).
                    const events = (r.events || []).filter(e => e.kind !== 'scheduled' && e.kind !== 'appointment_updated');
                    const isExp = !!historyOpen[r.inquiry_id];
                    const hidden = Math.max(0, events.length - HISTORY_HEAD);
                    const visible = (isExp || hidden === 0) ? events : events.slice(-HISTORY_HEAD);
                    return (
                        <div key={r.inquiry_id} className="maintenance-user-item">
                            {r.image
                                ? <img className="maintenance-user-thumb" src={r.image} alt={r.product} />
                                : <div className="maintenance-user-thumb maintenance-user-thumb--empty" />}
                            <div className="maintenance-user-body">
                                <div className="maintenance-user-name">{r.product || 'Inquiry'}</div>
                                <div className="maintenance-user-issue">{meetingLabel(r.meeting_type)}</div>
                                {/* The scheduled time, once agreed (read-only). */}
                                {r.appointment_time && (
                                    <div className="inquiry-user-appt-top">Scheduled · {fmt(r.appointment_time)}</div>
                                )}

                                <div className="inquiry-thread">
                                    {events.length > 0 && (
                                        <div className="inquiry-timeline">
                                            {hidden > 0 && (
                                                <button type="button" className="inquiry-tl-toggle"
                                                    onClick={() => setHistoryOpen(m => ({ ...m, [r.inquiry_id]: !isExp }))}>
                                                    {isExp ? 'Hide conversation' : `Show conversation (${hidden})`}
                                                </button>
                                            )}
                                            {visible.map((e, i) => {
                                                const you = e.actor === 'customer';
                                                const who = you ? 'You' : (r.merchant || 'Boutique');
                                                const dot = you ? 'grey' : 'black';
                                                return (
                                                    <div className="inquiry-ev" key={`${e.created_at}-${e.kind}-${i}`}>
                                                        <span className={`inquiry-ev-dot inquiry-ev-dot--${dot}`} />
                                                        <div className="inquiry-ev-body">
                                                            <div className="inquiry-ev-top">
                                                                <span className="inquiry-ev-who">{who}</span>
                                                                <span className="inquiry-ev-time">{fmt(e.created_at)}</span>
                                                            </div>
                                                            {e.body && <div className="inquiry-ev-text">“{e.body}”</div>}
                                                            {e.kind === 'completed' && <div className="inquiry-ev-note">Resolved</div>}
                                                            {e.kind === 'cancelled' && <div className="inquiry-ev-note">Cancelled</div>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <div className="inquiry-reply-dock">
                                        {/* Integrated composer — textarea + Send share one bordered box,
                                            matching the brand dashboard (see BrandInquiries .inquiry-compose). */}
                                        <div className="inquiry-compose">
                                            <textarea className="inquiry-compose-text" rows={1}
                                                ref={el => el && autoGrow(el)}
                                                value={msgText(r)}
                                                onChange={e => { setMsgs(m => ({ ...m, [r.inquiry_id]: e.target.value })); autoGrow(e.target); }} />
                                            <button type="button" className="inquiry-send" disabled={!msgText(r).trim()}
                                                onClick={() => sendMsg(r)} aria-label="Send">
                                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                                    <path d="M3 5l18 7-18 7 4-7-4-7z" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="inquiry-user-actions">
                                        <button className="catalogue-btn-secondary" onClick={() => patch(r.inquiry_id, { action: 'cancel' })}>Cancel inquiry</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default UserInquiries;
